import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { PushNotificationService } from '../../services/push-notification.service';
import { IosNotificationService } from '../../services/ios-notification.service';
import { IosNotificationGuideComponent } from '../ios-notification-guide/ios-notification-guide.component';

@Component({
  selector: 'app-pwa-install',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatSnackBarModule, IosNotificationGuideComponent],
  template: `
    <div class="pwa-install-banner" *ngIf="showInstallBanner && !isStandalone">
      <div class="banner-content">
        <mat-icon>get_app</mat-icon>
        <div class="banner-text">
          <h3>Installer Vendeur.site</h3>
          <p>Installez l'application pour un accès rapide et une expérience optimale</p>
        </div>
        <div class="banner-actions">
          <button mat-button (click)="dismissBanner()">Plus tard</button>
          <button mat-button (click)="neverShowBanner()" class="never-btn">Ne plus afficher</button>
          <button mat-raised-button color="primary" (click)="installPWA()">
            Installer
          </button>
        </div>
      </div>
    </div>

    <div class="notification-prompt" *ngIf="showNotificationPrompt && isStandalone">
      <div class="prompt-content">
        <mat-icon>notifications</mat-icon>
        <div class="prompt-text">
          <h4>Activer les notifications</h4>
          <p>Recevez des alertes pour vos commandes et mises à jour importantes</p>
        </div>
        <div class="prompt-actions">
          <button mat-button (click)="dismissNotificationPrompt()">Non merci</button>
          <button mat-raised-button color="primary" (click)="enableNotifications()">
            Activer
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .pwa-install-banner {
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: white;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      padding: 16px;
      z-index: 1000;
      max-width: 90%;
      width: 400px;
      animation: slideUp 0.3s ease-out;
    }

    .banner-content {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .banner-content mat-icon {
      font-size: 32px;
      width: 32px;
      height: 32px;
      color: #1976d2;
    }

    .banner-text {
      flex: 1;
    }

    .banner-text h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 500;
      color: #333;
    }

    .banner-text p {
      margin: 4px 0 0;
      font-size: 14px;
      color: #666;
    }

    .banner-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }

    .never-btn {
      color: #999 !important;
      font-size: 12px;
    }

    .notification-prompt {
      position: fixed;
      top: 70px;
      right: 20px;
      background: white;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      padding: 16px;
      z-index: 1000;
      max-width: 350px;
      animation: slideIn 0.3s ease-out;
    }

    .prompt-content {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .prompt-content mat-icon {
      font-size: 28px;
      width: 28px;
      height: 28px;
      color: #1976d2;
    }

    .prompt-text h4 {
      margin: 0;
      font-size: 16px;
      font-weight: 500;
      color: #333;
    }

    .prompt-text p {
      margin: 4px 0 0;
      font-size: 14px;
      color: #666;
    }

    .prompt-actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 8px;
    }

    @keyframes slideUp {
      from {
        transform: translateX(-50%) translateY(100px);
        opacity: 0;
      }
      to {
        transform: translateX(-50%) translateY(0);
        opacity: 1;
      }
    }

    @keyframes slideIn {
      from {
        transform: translateX(100px);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    @media (max-width: 600px) {
      .pwa-install-banner {
        width: calc(100% - 32px);
        bottom: 16px;
      }

      .notification-prompt {
        right: 16px;
        left: 16px;
        max-width: none;
      }

      .banner-content {
        flex-direction: column;
        text-align: center;
      }

      .banner-actions {
        width: 100%;
        justify-content: center;
      }
    }
  `]
})
export class PwaInstallComponent implements OnInit, OnDestroy {
  showInstallBanner = false;
  showNotificationPrompt = false;
  isStandalone = false;
  private deferredPrompt: any;

  constructor(
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private pushNotificationService: PushNotificationService,
    private iosNotificationService: IosNotificationService
  ) {}

  ngOnInit() {
    // Vérifier si l'app est en mode standalone
    this.checkStandaloneMode();
    
    // Écouter l'événement d'installation
    this.listenForInstallPrompt();
    
    // Vérifier si on doit afficher la bannière d'installation
    this.checkInstallBanner();
    
    // Vérifier les notifications après installation
    if (this.isStandalone) {
      this.checkNotificationPermission();
    }
  }

  ngOnDestroy() {
    // Nettoyer les event listeners si nécessaire
  }

  private checkStandaloneMode() {
    // Vérifier si l'app est en mode standalone
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as any).standalone
      || document.referrer.includes('android-app://');
    
    this.isStandalone = isInStandaloneMode;
  }

  private listenForInstallPrompt() {
    // Écouter l'événement beforeinstallprompt
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this.showInstallBanner = true;
    });

    // Écouter l'événement custom pwainstallable
    window.addEventListener('pwainstallable', () => {
      this.deferredPrompt = (window as any).deferredPrompt;
      if (this.deferredPrompt && !this.hasUserDismissedBanner()) {
        this.showInstallBanner = true;
      }
    });

    // Écouter l'événement appinstalled
    window.addEventListener('appinstalled', () => {
      this.showInstallBanner = false;
      this.deferredPrompt = null;
      this.snackBar.open('Application installée avec succès!', 'OK', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
    });
  }

  private checkInstallBanner() {
    // Vérifier si définitivement désactivé
    if (localStorage.getItem('pwa-install-never-show') === 'true') {
      this.showInstallBanner = false;
      return;
    }
    
    // Vérifier si l'utilisateur a déjà refusé l'installation
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
    
    // Réafficher après 90 jours (3 mois) seulement
    if (dismissedTime && daysSinceDismissed < 90) {
      this.showInstallBanner = false;
    }
  }

  private checkNotificationPermission() {
    // Vérifier si les notifications sont déjà autorisées
    if (this.pushNotificationService.getPermissionStatus() === 'default') {
      // Attendre un peu avant de demander les notifications
      setTimeout(() => {
        const notificationDismissed = localStorage.getItem('notification-prompt-dismissed');
        if (!notificationDismissed) {
          this.showNotificationPrompt = true;
        }
      }, 5000);
    }
  }

  private hasUserDismissedBanner(): boolean {
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (!dismissed) return false;
    
    const dismissedTime = parseInt(dismissed, 10);
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
    
    return daysSinceDismissed < 90; // 90 jours (3 mois)
  }

  async installPWA() {
    if (!this.deferredPrompt) {
      console.log('Prompt d\'installation non disponible');
      return;
    }

    // Afficher le prompt d'installation
    this.deferredPrompt.prompt();
    
    // Attendre la réponse de l'utilisateur
    const { outcome } = await this.deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('L\'utilisateur a accepté l\'installation');
      this.snackBar.open('Installation en cours...', '', {
        duration: 2000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
    } else {
      console.log('L\'utilisateur a refusé l\'installation');
      // Marquer comme refusé
      localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    }
    
    // Réinitialiser le prompt
    this.deferredPrompt = null;
    this.showInstallBanner = false;
  }

  dismissBanner() {
    this.showInstallBanner = false;
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  }

  neverShowBanner() {
    this.showInstallBanner = false;
    localStorage.setItem('pwa-install-never-show', 'true');
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  }

  async enableNotifications() {
    // Toujours déclencher le prompt natif `Notification.requestPermission()`
    // en premier — c'est le seul moyen pour que la PWA apparaisse dans
    // Réglages > Notifications (iOS / Android / Windows / macOS).
    // L'ancien chemin via IosNotificationService affichait des snackbars
    // contenant du HTML brut non interprété par MatSnackBar et bypassait
    // souvent le prompt natif sur iOS ≥ 16.4 PWA installée.
    if (typeof Notification === 'undefined') {
      this.snackBar.open(
        'Ce navigateur ne supporte pas les notifications natives.',
        'OK',
        { duration: 4000, horizontalPosition: 'center', verticalPosition: 'bottom' },
      );
      this.showNotificationPrompt = false;
      return;
    }

    const envInfo = this.iosNotificationService.getEnvironmentInfo();
    if (envInfo.isIOS && envInfo.requiresInstallation) {
      this.showNotificationPrompt = false;
      this.openIosGuide({ requiresInstall: true, isDenied: false });
      return;
    }

    try {
      // Si la permission est déjà `denied` sur iOS, on ne perd pas de
      // temps à appeler requestPermission (Safari ne re-affiche pas le
      // prompt — il renvoie immédiatement `denied`). On ouvre directement
      // le guide visuel des Réglages iOS.
      const currentPerm = Notification.permission;
      if (envInfo.isIOS && currentPerm === 'denied') {
        this.showNotificationPrompt = false;
        this.openIosGuide({ requiresInstall: false, isDenied: true });
        return;
      }

      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        await this.pushNotificationService.subscribeToNotifications();
        this.showNotificationPrompt = false;
        this.snackBar.open('Notifications activées avec succès !', 'OK', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });
        return;
      }

      if (permission === 'denied') {
        this.showNotificationPrompt = false;
        if (envInfo.isIOS) {
          this.openIosGuide({ requiresInstall: false, isDenied: true });
          return;
        }
        this.snackBar.open(
          'Notifications refusées. Réactivez-les depuis l\'icône 🔒 dans la barre d\'adresse.',
          'OK',
          { duration: 6000, horizontalPosition: 'center', verticalPosition: 'bottom' },
        );
        return;
      }

      this.snackBar.open(
        'Aucune réponse au prompt. Cliquez à nouveau pour réessayer.',
        'OK',
        { duration: 4000, horizontalPosition: 'center', verticalPosition: 'bottom' },
      );
    } catch (error) {
      console.error('[push] enable notifications failed', error);
      this.snackBar.open(
        'Impossible d\'activer les notifications (' + ((error as any)?.message || 'erreur inconnue') + ')',
        'OK',
        { duration: 5000, horizontalPosition: 'center', verticalPosition: 'bottom' },
      );
    }
  }

  private openIosGuide(opts: { requiresInstall: boolean; isDenied: boolean }): void {
    this.dialog.open(IosNotificationGuideComponent, {
      panelClass: 'vd-dialog-host',
      maxWidth: '100vw',
      data: {
        appName: 'Vendeur',
        isDenied: opts.isDenied,
        requiresInstall: opts.requiresInstall,
      },
    });
  }

  dismissNotificationPrompt() {
    this.showNotificationPrompt = false;
    localStorage.setItem('notification-prompt-dismissed', Date.now().toString());
  }
}